import { useEffect, useState } from 'react';
import {
  BanIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  DownloadIcon,
  FileAudioIcon,
  Loader2Icon,
  RotateCcwIcon,
  XCircleIcon,
} from 'lucide-react';
import {
  chatAudioTranscriptionBatchDataSchema,
  type AudioArtifactDTO,
  type AudioTranscriptionBatchStatusResponseDTO,
  type AudioTranscriptionJobStatusItemDTO,
} from '@shared/schemas/audio-transcription';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getChatErrorMessage } from '../chat-errors';
import {
  formatAudioDuration,
  getAudioTranscriptionHeader,
  getAudioTranscriptionPhaseLabel,
  getAudioTranscriptionSummary,
  isAudioTranscriptionTerminalStatus,
} from '../audio-transcription-ui';
import { useAudioTranscription } from '../hooks/useAudioTranscription';
import { getAudioArtifactDownload } from '../services/audio-transcription.api';
import { ChatAudioTranscriptPanel } from './ChatAudioTranscriptPanel';

type ChatAudioTranscriptionBatchProps = {
  data: unknown;
};

async function triggerFileDownload(downloadUrl: string, filename: string) {
  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error('download_failed');
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function JobStatusIcon({ job }: { job: AudioTranscriptionJobStatusItemDTO }) {
  if (job.status === 'succeeded') {
    return <CheckCircle2Icon aria-hidden className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />;
  }
  if (job.status === 'failed' || job.status === 'dead') {
    return <XCircleIcon aria-hidden className="size-3.5 shrink-0 text-destructive" />;
  }
  if (job.status === 'canceled') {
    return <BanIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />;
  }
  return (
    <Loader2Icon
      aria-hidden
      className="size-3.5 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none"
    />
  );
}

function findJobDocx(artifacts: AudioArtifactDTO[], jobId: string) {
  return artifacts.find((artifact) => artifact.kind === 'file_docx' && artifact.jobId === jobId) ?? null;
}

function findCombinedDocx(artifacts: AudioArtifactDTO[]) {
  return artifacts.find((artifact) => artifact.kind === 'combined_docx') ?? null;
}

function AudioJobRow({
  batch,
  isBusy,
  onDownload,
  onOpenTranscript,
  onRetry,
}: {
  batch: AudioTranscriptionBatchStatusResponseDTO;
  isBusy: boolean;
  onDownload: (artifact: AudioArtifactDTO) => void;
  onOpenTranscript: (job: AudioTranscriptionJobStatusItemDTO) => void;
  onRetry: (jobId: string) => void;
}) {
  return (
    <ul className="flex flex-col gap-3">
      {batch.jobs.map((job) => {
        const phaseLabel = getAudioTranscriptionPhaseLabel({
          phase: job.phase,
          status: job.status,
        });
        const durationLabel = formatAudioDuration(job.durationMs);
        const showProgress = job.phase === 'transcribing'
          && job.status === 'processing'
          && job.chunkCount != null
          && job.chunkCount > 0;
        const progressValue = showProgress
          ? Math.round(((job.processedChunkCount ?? 0) / job.chunkCount!) * 100)
          : 0;
        const docx = findJobDocx(batch.artifacts, job.jobId);
        const canRetry = job.status === 'failed' || job.status === 'dead';
        const canView = job.status === 'succeeded';

        return (
          <li
            className="rounded-[var(--radius-surface-2xl)] border border-border/60 bg-background/80 px-3 py-2.5"
            key={job.jobId}
          >
            <div className="flex min-w-0 items-start gap-2.5">
              <FileAudioIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <p className="truncate text-sm font-medium text-foreground">{job.filename}</p>
                  {durationLabel ? (
                    <span className="text-xs text-muted-foreground">{durationLabel}</span>
                  ) : null}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <JobStatusIcon job={job} />
                  <span>{phaseLabel}</span>
                </div>
                {showProgress ? (
                  <Progress
                    aria-label={`Transcription de ${job.filename}`}
                    className="h-1.5"
                    value={progressValue}
                  />
                ) : null}
                {job.errorCode ? (
                  <p className="text-xs text-destructive">{getChatErrorMessage({ code: job.errorCode })}</p>
                ) : null}
                {job.preview ? (
                  <p className="line-clamp-2 text-xs text-muted-foreground">{job.preview}</p>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-0.5">
                  {canView ? (
                    <Button
                      onClick={() => onOpenTranscript(job)}
                      size="xs"
                      type="button"
                      variant="outline"
                    >
                      Voir la transcription
                    </Button>
                  ) : null}
                  {docx ? (
                    <Button
                      disabled={isBusy}
                      onClick={() => onDownload(docx)}
                      size="xs"
                      type="button"
                      variant="outline"
                    >
                      <DownloadIcon className="size-3" data-icon="inline-start" />
                      Télécharger Word
                    </Button>
                  ) : null}
                  {canRetry ? (
                    <Button
                      data-testid="audio-transcription-retry-job"
                      disabled={isBusy}
                      onClick={() => onRetry(job.jobId)}
                      size="xs"
                      type="button"
                      variant="outline"
                    >
                      <RotateCcwIcon className="size-3" data-icon="inline-start" />
                      Réessayer
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function ChatAudioTranscriptionBatch({ data }: ChatAudioTranscriptionBatchProps) {
  const parsed = chatAudioTranscriptionBatchDataSchema.safeParse(data);
  const batchId = parsed.success ? parsed.data.batchId : undefined;
  const fallbackTitle = parsed.success ? parsed.data.title : 'Transcription audio';
  const transcription = useAudioTranscription(batchId);
  const batch = transcription.status;
  const [isOpen, setIsOpen] = useState(true);
  const [hasAutoCollapsed, setHasAutoCollapsed] = useState(false);
  const [activeTranscript, setActiveTranscript] = useState<{
    filename: string;
    jobId: string;
  } | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (batch?.status === 'succeeded' && !hasAutoCollapsed) {
      setIsOpen(false);
      setHasAutoCollapsed(true);
    }
  }, [batch?.status, hasAutoCollapsed]);

  if (!parsed.success) {
    return null;
  }

  const isActive = Boolean(batch && !isAudioTranscriptionTerminalStatus(batch.status));
  const isFailed = batch?.status === 'failed' || transcription.isError;
  const isPartial = batch?.status === 'partially_succeeded';
  const isSucceeded = batch?.status === 'succeeded';
  const isCanceled = batch?.status === 'canceled';
  const combinedDocx = batch ? findCombinedDocx(batch.artifacts) : null;
  const canCancel = isActive && batch?.status !== 'cancel_requested';
  const canRetryAnalysis = Boolean(
    batch && (batch.status === 'failed' || batch.analysisErrorCode),
  );
  const summary = batch
    ? getAudioTranscriptionSummary({
      succeededJobs: batch.succeededJobs,
      totalJobs: batch.totalJobs,
    })
    : 'Préparation du traitement…';
  const header = batch
    ? getAudioTranscriptionHeader(batch.totalJobs)
    : fallbackTitle;

  const handleDownload = async (artifact: AudioArtifactDTO) => {
    if (!batchId || isDownloading) {
      return;
    }
    setIsDownloading(true);
    try {
      const result = await getAudioArtifactDownload(batchId, artifact.artifactId);
      await triggerFileDownload(result.downloadUrl, result.filename);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      await transcription.retryJob(jobId);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    }
  };

  const handleRetryAnalysis = async () => {
    try {
      await transcription.retryAnalysis();
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    }
  };

  const handleCancel = async () => {
    try {
      await transcription.cancel();
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    }
  };

  const cardClassName = cn(
    'w-full max-w-xl rounded-[var(--radius-surface-2xl)] border border-border/70 bg-card/90 px-3.5 py-3 text-left',
    isActive && 'opacity-90',
    isFailed && 'border-destructive/25 bg-destructive/5',
  );

  return (
    <>
      <section
        aria-busy={isActive || transcription.isFetching}
        className={cardClassName}
        data-testid="audio-transcription-batch"
      >
        <Collapsible onOpenChange={setIsOpen} open={isOpen}>
          <div className="flex items-start gap-3">
            <FileAudioIcon aria-hidden className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <p className="truncate text-sm font-semibold text-foreground">{header}</p>
                <CollapsibleTrigger asChild>
                  <Button
                    aria-label={isOpen ? 'Réduire le détail' : 'Afficher le détail'}
                    className="size-7 shrink-0"
                    size="icon-sm"
                    type="button"
                    variant="ghost"
                  >
                    <ChevronDownIcon className={cn('size-4 transition-transform motion-reduce:transition-none', !isOpen && '-rotate-90')} />
                  </Button>
                </CollapsibleTrigger>
              </div>
              <p aria-atomic="true" aria-live="polite" className="text-xs text-muted-foreground">
                {summary}
              </p>
              {isSucceeded ? (
                <Badge
                  className="gap-1 border-emerald-500/20 bg-emerald-500/[0.07] px-2 py-0.5 text-[11px] font-medium text-emerald-800 shadow-none dark:text-emerald-100"
                  data-testid="audio-transcription-batch-badge"
                  variant="outline"
                >
                  <CheckCircle2Icon className="size-3 text-emerald-600 dark:text-emerald-400" />
                  Terminé
                </Badge>
              ) : null}
              {isPartial ? (
                <Badge className="gap-1" data-testid="audio-transcription-batch-badge" variant="outline">
                  Succès partiel
                </Badge>
              ) : null}
              {isFailed ? (
                <Badge className="gap-1" data-testid="audio-transcription-batch-badge" variant="destructive">
                  <XCircleIcon className="size-3" />
                  Échec
                </Badge>
              ) : null}
              {isCanceled ? (
                <Badge className="gap-1" data-testid="audio-transcription-batch-badge" variant="outline">
                  <BanIcon className="size-3" />
                  Annulé
                </Badge>
              ) : null}
            </div>
          </div>

          <CollapsibleContent className="pt-3">
            {transcription.isPending && !batch ? (
              <div className="space-y-2" data-testid="audio-transcription-batch-loading">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : null}

            {transcription.isError && !batch ? (
              <p className="text-sm text-destructive">{getChatErrorMessage(transcription.error)}</p>
            ) : null}

            {batch ? (
              <AudioJobRow
                batch={batch}
                isBusy={isDownloading}
                onDownload={(artifact) => void handleDownload(artifact)}
                onOpenTranscript={(job) => setActiveTranscript({
                  filename: job.filename,
                  jobId: job.jobId,
                })}
                onRetry={(jobId) => void handleRetryJob(jobId)}
              />
            ) : null}

            {batch?.sourceExpired ? (
              <p className="mt-3 text-xs text-muted-foreground">
                Audio source supprimé conformément à la politique de conservation.
              </p>
            ) : null}

            {batch?.analysisErrorCode ? (
              <p className="mt-2 text-xs text-destructive">
                {getChatErrorMessage({ code: batch.analysisErrorCode })}
              </p>
            ) : null}

            <div className="mt-3 flex flex-wrap gap-2">
              {canCancel ? (
                <Button
                  onClick={() => void handleCancel()}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  Annuler
                </Button>
              ) : null}
              {canRetryAnalysis ? (
                <Button
                  data-testid="audio-transcription-retry-analysis"
                  onClick={() => void handleRetryAnalysis()}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <RotateCcwIcon className="size-3.5" data-icon="inline-start" />
                  Réessayer
                </Button>
              ) : null}
              {combinedDocx ? (
                <Button
                  data-testid="audio-transcription-download-docx"
                  disabled={isDownloading}
                  onClick={() => void handleDownload(combinedDocx)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <DownloadIcon className="size-3.5" data-icon="inline-start" />
                  Télécharger Word
                </Button>
              ) : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </section>

      {batchId && activeTranscript ? (
        <ChatAudioTranscriptPanel
          batchId={batchId}
          filename={activeTranscript.filename}
          jobId={activeTranscript.jobId}
          onOpenChange={(open) => {
            if (!open) {
              setActiveTranscript(null);
            }
          }}
          open
        />
      ) : null}
    </>
  );
}
